import { useMemo, useState, useSyncExternalStore } from 'react'
import { ChevronDown, Download } from 'lucide-react'
import { contractExpired, type PayoffLeg } from '@apna/shared'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { accountStore } from '@/store/account'
import { useTickCell } from '@/feed/useTick'
import { SPOT_TOKENS } from '@/feed/spotTokens'
import { compactInr } from '@/screens/terminal/quantity'
import { PayoffChart } from '@/components/payoff/PayoffChart'
import { greeksLabel, groupGreeks } from './portfolio/groupGreeks'
import {
  byResult,
  forTab,
  groupPositions,
  toCsv,
  totalsOf,
  type PortfolioGroup,
  type PortfolioTab,
  type PositionRow,
  type ResultFilter,
} from './portfolio/portfolioModel'
import { usePortfolio } from './portfolio/usePortfolio'
import { IssuePanel } from './portfolio/IssuePanel'
import { ReviewPanel } from './portfolio/ReviewPanel'
import { useOrphanReview } from './portfolio/useOrphanReview'
import { useTradeIssues } from './portfolio/useTradeIssues'

/**
 * The Portfolio — ROADMAP 6.9, with 6.8's payoff inside it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY NUMBER ON THIS SCREEN KNOWS WHETHER IT IS COMPLETE
 *
 * The totals, the group headers and the rows all come from the same marks,
 * and an unpriced leg makes all three incomplete together. Each one says so
 * rather than quietly summing what it has — a portfolio total is the single
 * most trusted figure in the product and the easiest place for a silent
 * omission to survive.
 *
 * GROUPED BY UNDERLYING AND EXPIRY, because that is the unit a payoff curve
 * is valid for. See `portfolioModel.ts`.
 *
 * BOOKED IS GROSS. Net is derived once, by `netPnl`, and the two are shown
 * side by side with charges between them so the difference is always
 * explainable — which is Phase 6's exit criterion, on screen.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export default function Portfolio() {
  useSyncExternalStore(accountStore.subscribe, accountStore.getSnapshot, accountStore.getSnapshot)
  const connectionId = accountStore.getSnapshot().selectedId

  const [tab, setTab] = useState<PortfolioTab>('RUNNING')
  const [filter, setFilter] = useState<ResultFilter>('ALL')
  const [openKey, setOpenKey] = useState<string | null>(null)

  const portfolio = usePortfolio(connectionId)
  /*
   * 7.8. Renders nothing at all when nothing is flagged, which is the normal
   * case — see `ReviewPanel` for why that matters more than it sounds.
   */
  const review = useOrphanReview(connectionId)
  /*
   * 7.9. Also renders nothing when every basket came up whole, which is the
   * normal case. Above the totals for the same reason the review is: a failed
   * leg is excluded from them.
   */
  const issues = useTradeIssues(connectionId)
  const ready = portfolio.kind === 'READY'
  const allRows = ready ? portfolio.rows : []

  const visible = useMemo(
    () => byResult(forTab(allRows, tab), filter),
    [allRows, tab, filter],
  )
  const groups = useMemo(() => groupPositions(visible), [visible])
  const totals = useMemo(() => totalsOf(visible), [visible])

  const counts = useMemo(
    () => ({
      RUNNING: forTab(allRows, 'RUNNING').length,
      SQUARED_OFF: forTab(allRows, 'SQUARED_OFF').length,
      OVERALL: forTab(allRows, 'OVERALL').length,
    }),
    [allRows],
  )

  return (
    <div className="h-full space-y-3 overflow-auto p-3" data-testid="portfolio-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Portfolio</h1>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Rebuilt from our own fill journal. The broker&apos;s book is a reconciliation check,
            not the source.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/*
            The MARKS are live — unbooked P&L moves with every tick. What
            goes stale is the JOURNAL: booked P&L, charges, and any position
            opened since this screen loaded. So the refresh is manual and
            says what it refreshes, rather than a poll that re-reads the whole
            fold every few seconds for numbers that change when a fill lands
            and at no other time.
          */}
          <button
            type="button"
            data-testid="portfolio-refresh"
            onClick={portfolio.refresh}
            disabled={portfolio.kind === 'LOADING'}
            title="Re-read the fill journal. Prices are already live."
            className="h-8 rounded-lg border border-border px-3 text-[11px] font-bold text-muted-foreground transition-all hover:bg-secondary hover:text-foreground disabled:opacity-40"
          >
            {portfolio.kind === 'LOADING' ? 'Reading…' : 'Refresh'}
          </button>
          <button
            type="button"
            data-testid="portfolio-export"
            disabled={visible.length === 0}
            onClick={() => downloadCsv(visible, portfolio.ltpOf)}
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-[11px] font-bold text-muted-foreground transition-all hover:bg-secondary hover:text-foreground disabled:opacity-40"
          >
            <Download size={12} /> CSV
          </button>
        </div>
      </div>

      {/*
        ABOVE the totals, deliberately. Every flagged leg is excluded from
        them, so the caveat has to be read before the numbers are.
      */}
      <ReviewPanel review={review} />
      <IssuePanel issues={issues} />

      {/*
        EVERY TILE IS A DASH UNTIL THE JOURNAL HAS BEEN READ.
        `totalsOf([])` is a COMPLETE zero, correctly — an empty portfolio is
        genuinely flat — but on a cold load "no rows yet" and "no P&L" are
        the same empty array, and a tile reading +₹0 while the request is
        still in flight is the most trusted number on the screen being wrong.
        The totals are shown only once there is an answer to show.
      */}
      <div className="flex flex-wrap gap-2">
        <Stat label="NET" value={ready ? totals.net : null} accent sub="booked + unbooked − charges" />
        <Stat label="BOOKED" value={ready ? totals.booked : null} sub="gross, before charges" />
        <Stat
          label="UNBOOKED"
          value={!ready || (totals.marked === 0 && totals.unmarked > 0) ? null : totals.unbooked}
          sub={
            ready && totals.unmarked > 0
              ? `${totals.marked} of ${totals.marked + totals.unmarked} marked`
              : 'mark to market'
          }
          warn={ready && totals.unmarked > 0}
        />
        <Stat label="CHARGES" value={ready ? -totals.charges : null} sub="brokerage, taxes, stamp" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-border p-0.5" data-testid="portfolio-tabs">
          {(['RUNNING', 'SQUARED_OFF', 'OVERALL'] as const).map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`portfolio-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={cn(
                'h-8 rounded-md px-4 text-[11px] font-extrabold tracking-wide transition-all',
                tab === t ? 'bg-secondary text-foreground' : 'text-muted-foreground',
              )}
            >
              {TAB_LABEL[t]}
              <span className="num ml-1.5 text-[9px] opacity-60">{counts[t]}</span>
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-border p-0.5">
          {(['ALL', 'WINNERS', 'LOSERS'] as const).map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={cn(
                'h-8 rounded-md px-3 text-[10px] font-bold transition-all',
                filter === f ? 'bg-secondary text-foreground' : 'text-muted-foreground',
              )}
            >
              {f === 'ALL' ? 'All' : f === 'WINNERS' ? 'Winners' : 'Losers'}
            </button>
          ))}
        </div>
        {totals.unmarked > 0 && (
          <span
            className="num rounded bg-warning/15 px-2 py-1 text-[9.5px] font-bold text-warning"
            data-testid="portfolio-unmarked"
          >
            {totals.unmarked} unpriced
          </span>
        )}
      </div>

      <Body
        state={portfolio}
        groups={groups}
        openKey={openKey}
        onToggle={setOpenKey}
        connectionId={connectionId}
        ltpOf={portfolio.ltpOf}
        onExited={portfolio.refresh}
      />
    </div>
  )
}

/**
 * Colour for a money figure.
 *
 * A zero is NEUTRAL, not green. An open position books nothing until part of
 * it closes, so a table of green zeroes is the common case, and it trains
 * the eye to stop reading the colour at all.
 */
function toneOf(v: number | null): string {
  if (v === null || v === 0) return 'text-muted-foreground'
  return v > 0 ? 'text-success' : 'text-destructive'
}

const TAB_LABEL: Record<PortfolioTab, string> = {
  RUNNING: 'Running',
  SQUARED_OFF: 'Squared off',
  OVERALL: 'Overall',
}

/**
 * A figure, or an honest absence.
 *
 * `null` renders as a dash. A zero is a real P&L and must never stand for
 * "we could not price this".
 */
function Stat({
  label,
  value,
  sub,
  accent,
  warn,
}: {
  label: string
  value: number | null
  sub?: string
  accent?: boolean
  warn?: boolean
}) {
  const id = `stat-${label.toLowerCase().replace(/ /g, '-')}`
  return (
    <div className="qbar-card min-w-[150px] px-4 py-3">
      <div className="text-[9px] font-bold tracking-[0.16em] text-muted-foreground">{label}</div>
      <div
        className={cn(
          'num mt-1 text-lg font-bold',
          toneOf(value),
          accent && 'text-xl',
          warn && 'opacity-80',
        )}
        data-testid={id}
      >
        {value === null ? '—' : `${value >= 0 ? '+' : '-'}₹${compactInr(Math.abs(value))}`}
      </div>
      {sub && <div className="mt-0.5 text-[9.5px] text-muted-foreground">{sub}</div>}
    </div>
  )
}

function Body({
  state,
  groups,
  openKey,
  onToggle,
  connectionId,
  ltpOf,
  onExited,
}: {
  state: ReturnType<typeof usePortfolio>
  groups: PortfolioGroup[]
  openKey: string | null
  onToggle: (key: string | null) => void
  connectionId: string | null
  ltpOf: (t: number | null) => number | null
  onExited: () => void
}) {
  if (state.kind === 'IDLE') return <Note>Choose an account to see its portfolio.</Note>
  if (state.kind === 'LOADING') return <Note>Reading the fill journal…</Note>
  if (state.kind === 'UNREADABLE') {
    return (
      <div
        className="qbar-card border-destructive/40 px-4 py-3 text-[12px] text-destructive"
        data-testid="portfolio-unreadable"
      >
        The portfolio could not be read: {state.why}
        <div className="mt-1 text-[10.5px] text-muted-foreground">
          This is not an empty portfolio. Nothing has been concluded about your positions.
        </div>
      </div>
    )
  }
  if (groups.length === 0) {
    return <Note>Nothing here. Positions appear once fills land in the journal.</Note>
  }

  return (
    <div className="space-y-2">
      {groups.map((g) => (
        <GroupCard
          key={g.key}
          group={g}
          open={openKey === g.key}
          onToggle={() => onToggle(openKey === g.key ? null : g.key)}
          connectionId={connectionId}
          ltpOf={ltpOf}
          onExited={onExited}
        />
      ))}
    </div>
  )
}

const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="qbar-card px-4 py-6 text-center text-[12px] text-muted-foreground">{children}</div>
)

function GroupCard({
  group,
  open,
  onToggle,
  connectionId,
  ltpOf,
  onExited,
}: {
  group: PortfolioGroup
  open: boolean
  onToggle: () => void
  connectionId: string | null
  ltpOf: (t: number | null) => number | null
  onExited: () => void
}) {
  // The index's own token, so the payoff's spot marker is the underlying and
  // not one of the legs.
  const spotToken = SPOT_TOKENS[group.underlying] ?? 0
  const { value: spot } = useTickCell(spotToken, 'ltp')
  const liveSpot = spot > 0 ? spot : null

  const legs = useMemo(
    (): PayoffLeg[] =>
      group.rows
        .filter((r) => r.open)
        .map((r) => ({
          netQty: r.netQty,
          avgPrice: r.avgPrice,
          optionType: r.optionType === 'CE' || r.optionType === 'PE' ? r.optionType : null,
          strike: r.strike,
        })),
    [group.rows],
  )

  /*
   * Solved only while the card is OPEN. Each pass bisects an IV per leg, and
   * a collapsed card is work nobody is reading — on a portfolio of twenty
   * groups that is twenty solves a render for one visible chart.
   */
  const greeks = useMemo(
    () => (open ? groupGreeks(group.rows, ltpOf, liveSpot) : null),
    [open, group.rows, ltpOf, liveSpot],
  )

  const openTokens = group.rows.filter((r) => r.open && r.token !== null).map((r) => r.token as number)

  return (
    <section className="qbar-card overflow-hidden" data-testid={`pfgroup-${group.key}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/30"
      >
        <div className="min-w-[190px]">
          <div className="text-[12.5px] font-bold">{group.title}</div>
          <div className="num mt-0.5 text-[10px] text-muted-foreground">
            {group.expiryLabel} · {group.rows.length} leg{group.rows.length === 1 ? '' : 's'}
            {group.openRows < group.rows.length && ` · ${group.openRows} open`}
          </div>
        </div>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[8px] font-extrabold tracking-widest',
            group.openRows > 0
              ? 'bg-success/15 text-success'
              : 'bg-secondary text-muted-foreground',
          )}
        >
          {group.openRows > 0 ? 'OPEN' : 'SQUARED OFF'}
        </span>
        {group.unmarked > 0 && (
          <span className="num rounded bg-warning/15 px-1.5 py-0.5 text-[8px] font-extrabold text-warning">
            {group.marked}/{group.marked + group.unmarked}
          </span>
        )}
        <div className="flex-1" />
        <div className="num text-right text-[11px]">
          {/*
            Greeks, when the position contains a call and a put at one strike
            to recover a forward from. When it does not there is no honest
            number — see `groupGreeks` — and the space is simply left empty
            rather than filled from the spot.
          */}
          {/*
            WHAT THIS POSITION IS BLOCKING, estimated by the exchange's own
            method — SPAN scan plus exposure (`margin.ts`). It is the figure
            a desk checks against the broker's funds screen, and until now
            the application could not say it at all.

            Shown only for a position that HOLDS a short. A long basket
            blocks its premium, which is already in the entry prices below.
          */}
          {greeks?.kind === 'OK' && greeks.margin.ok && greeks.margin.margin.scanRisk > 0 && (
            <span className="mr-4 text-muted-foreground" data-testid={`pfgroup-margin-${group.key}`}>
              margin ≈ ₹{compactInr(greeks.margin.margin.total)}
            </span>
          )}
          {greeks !== null && greeksLabel(greeks) !== null && (
            <span className="mr-4 text-muted-foreground" data-testid={`pfgroup-greeks-${group.key}`}>
              {greeksLabel(greeks)}
            </span>
          )}
          <span
            className={cn('text-[15px] font-bold', toneOf(group.net))}
            data-testid={`pfgroup-net-${group.key}`}
          >
            {group.net === null
              ? '—'
              : `${group.net >= 0 ? '+' : '-'}₹${Math.abs(Math.round(group.net)).toLocaleString('en-IN')}`}
          </span>
        </div>
        <ChevronDown
          size={15}
          className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="grid grid-cols-1 gap-4 border-t border-border p-4 lg:grid-cols-2">
          <div className="min-h-[200px]">
            {legs.length > 0 ? (
              <PayoffChart
                legs={legs}
                spot={liveSpot}
                t0={greeks?.kind === 'OK' ? greeks.valueAt : null}
                height={200}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground">
                Squared off — nothing left to plot.
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 text-[10px] font-bold tracking-[0.16em] text-muted-foreground">
              LEGS
            </div>
            <LegTable group={group} ltpOf={ltpOf} />
            {greeks !== null && greeks.kind === 'NONE' && legs.length > 0 && (
              <p
                className="mt-2 text-[10px] leading-[1.5] text-muted-foreground"
                data-testid={`pfgroup-nogreeks-${group.key}`}
              >
                {greeks.why}
              </p>
            )}
            {openTokens.length > 0 && (
              <ExitStrategy
                connectionId={connectionId}
                tokens={openTokens}
                groupKey={group.key}
                onExited={onExited}
              />
            )}
          </div>
        </div>
      )}
    </section>
  )
}

/** What `POST /market/exit` answers. Mirrors `ExitResult` on the server. */
type ExitResponse =
  | { ok: true; intentId: string; clientRequestId: string; legCount: number; skipped: { token: number; symbol: string | null; why: string }[] }
  | { ok: false; reason: string }

/**
 * Close every open leg of this group.
 *
 * SENDS WHICH, NEVER HOW MUCH. The same rule the drawer follows: these
 * numbers are from the last read, and a quantity sent from here could open an
 * opposite position if the holding had shrunk meanwhile. The server re-reads
 * and sizes it.
 *
 * The button stays disabled until the refresh completes, because the HTTP
 * reply means SENT and not filled — re-enabling on the reply invites a second
 * press against a position already on its way out, which is the bug that
 * reversed a position on 2026-10-12.
 */
function ExitStrategy({
  connectionId,
  tokens,
  groupKey,
  onExited,
}: {
  connectionId: string | null
  tokens: number[]
  groupKey: string
  onExited: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (): Promise<void> => {
    if (!connectionId || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<ExitResponse>('/market/exit', {
        connectionId,
        tokens,
        clientRequestId: `pf-exit-${groupKey}-${Date.now()}`,
      })
      if (!r.ok) {
        setError(r.reason)
        return
      }
      if (r.skipped.length > 0) {
        setError(
          `${r.legCount} leg(s) sent. Could not exit: ${r.skipped
            .map((s) => `${s.symbol ?? s.token} — ${s.why}`)
            .join('; ')}`,
        )
      }
      onExited()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'The exit request failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        data-testid={`portfolio-exit-${groupKey}`}
        onClick={() => void run()}
        disabled={busy || !connectionId}
        className="h-8 rounded-lg bg-destructive/15 px-4 text-[11px] font-extrabold text-destructive transition-all hover:bg-destructive hover:text-white disabled:opacity-40"
      >
        {busy ? 'EXITING…' : 'EXIT STRATEGY'}
      </button>
      {error !== null && (
        <p className="mt-1.5 text-[10px] text-destructive" data-testid={`portfolio-exit-error-${groupKey}`}>
          {error}
        </p>
      )}
    </div>
  )
}

function LegTable({
  group,
  ltpOf,
}: {
  group: PortfolioGroup
  ltpOf: (t: number | null) => number | null
}) {
  return (
    <table className="num w-full text-[11px]">
      <tbody>
        {group.rows.map((r) => (
          <LegRow key={r.tradeLegId} row={r} ltp={ltpOf(r.token)} />
        ))}
      </tbody>
    </table>
  )
}

function LegRow({ row, ltp }: { row: PositionRow; ltp: number | null }) {
  const money = (v: number | null): string =>
    v === null ? '—' : `${v < 0 ? '-' : ''}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`

  return (
    <tr className="border-b border-border/40" data-testid={`pfrow-${row.tradeLegId}`}>
      <td className="py-1.5">
        <span className={row.netQty < 0 ? 'text-destructive' : 'text-success'}>
          {row.open ? (row.netQty < 0 ? 'SELL' : 'BUY') : 'OUT'}
        </span>
      </td>
      <td className={row.optionType === 'CE' ? 'text-primary' : 'text-warning'}>
        {row.strike !== null
          ? `${row.strike.toLocaleString('en-IN')} ${row.optionType ?? ''}`
          : (row.tradingSymbol ?? row.tradeLegId)}
        {/*
          7.10. The badge is the TRADE's state now, not a per-row reconcile
          verdict: 6.3 compared every position on a timer, and 7.6–7.8 made
          the question state-dependent and per basket. Both of these mean
          automation should not act on this leg.
        */}
        {row.tradeStatus === 'NEEDS_REVIEW' && (
          <span
            className="ml-1 rounded bg-warning/15 px-1 py-0.5 text-[8px] font-extrabold text-warning"
            /*
             * The tooltip names WHICH of the two, because the fix differs: an
             * expired contract is gone for good and only its settlement price
             * is in question, while an orphan may still be sitting in the
             * broker's book under a position this software never placed.
             */
            title={
              row.netQty !== 0 && row.expiry !== null && contractExpired(row.expiry)
                ? 'This contract expired. We did not see how it settled, so nothing has been changed — confirm the review above.'
                : 'The broker no longer reports this position. Confirm the review above; nothing has been changed.'
            }
          >
            REVIEW
          </span>
        )}
        {row.legStatus === 'FAILED' && (
          <span
            className="ml-1 rounded bg-destructive/15 px-1 py-0.5 text-[8px] font-extrabold text-destructive"
            title="This leg never opened. Retry it above, or exit what is held."
          >
            FAILED
          </span>
        )}
      </td>
      {/* UNITS, not lots: a lot count is a sizing instruction, a quantity is
          what is held and what the broker's book is compared against. */}
      <td className="text-right text-muted-foreground">
        {row.open ? Math.abs(row.netQty).toLocaleString('en-IN') : '—'}
      </td>
      <td className="text-right text-muted-foreground">
        {row.open ? `@${row.avgPrice.toFixed(2)}` : '—'}
      </td>
      <td className="text-right">{ltp === null ? '—' : ltp.toFixed(2)}</td>
      <td className={cn('text-right font-bold', toneOf(row.net))}>{money(row.net)}</td>
    </tr>
  )
}

/**
 * Hand the CSV to the browser.
 *
 * A Blob and an object URL rather than a `data:` URI: a portfolio of a few
 * hundred rows exceeds what some browsers accept in a URL, and the failure
 * is silent — the click does nothing and the user assumes the export is
 * broken rather than too large.
 */
function downloadCsv(rows: readonly PositionRow[], ltpOf: (t: number | null) => number | null): void {
  const blob = new Blob([toCsv(rows, ltpOf)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `portfolio-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
